import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Form, Button, Row, Col, Alert, Spinner } from "react-bootstrap";
import { db } from "../firebase"; // Ensure your firebase.js exports 'db'
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// 1. Validation Schema
const schema = yup.object().shape({
  studentName: yup.string().required("Full name is required"),
  parentEmail: yup
    .string()
    .email("Invalid email")
    .required("Email is required"),
  parentPhone: yup.string().required("Phone number is required"),
  program: yup.string().required("Please select a program"),
  learningMode: yup.string().required("Please select a learning mode"),
  message: yup.string().max(500, "Message too long"),
});

const AdmissionsForm = () => {
  const [status, setStatus] = useState({ type: "", msg: "" });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // 2. Submit to Firebase
  const onSubmit = async (data) => {
    try {
      setStatus({ type: "", msg: "" });

      // Save to "applications" collection
      await addDoc(collection(db, "applications"), {
        ...data,
        submittedAt: serverTimestamp(),
        schoolName: "Viriya International School",
      });

      setStatus({
        type: "success",
        msg: "Thank you! Your application has been submitted successfully.",
      });
      reset(); // Clear form
    } catch (error) {
      console.error("Error saving to Firebase:", error);
      setStatus({
        type: "danger",
        msg: "Submission failed. Please try again later.",
      });
    }
  };

  return (
    <div className="admission-form-container">
      {status.msg && (
        <Alert
          variant={status.type}
          dismissible
          onClose={() => setStatus({ type: "", msg: "" })}
        >
          {status.msg}
        </Alert>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          {/* Student Name */}
          <Col md={12} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">Student Full Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter student's name"
                isInvalid={!!errors.studentName}
                {...register("studentName")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.studentName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Contact Details */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">Parent Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="email@example.com"
                isInvalid={!!errors.parentEmail}
                {...register("parentEmail")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.parentEmail?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="09..."
                isInvalid={!!errors.parentPhone}
                {...register("parentPhone")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.parentPhone?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Program Selection */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">Interested Program</Form.Label>
              <Form.Select
                isInvalid={!!errors.program}
                {...register("program")}
              >
                <option value="">Choose...</option>
                <option value="Early Years">Early Years (KG)</option>
                <option value="Primary & Secondary">Primary & Secondary</option>
                <option value="Upper Secondary">Upper Secondary (IGCSE)</option>
                <option value="A Level">Cambridge Advanced Level</option>
                <option value="GED">GED Program</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.program?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          {/* Learning Mode */}
          <Col md={6} className="mb-3">
            <Form.Group>
              <Form.Label className="fw-bold">
                Preferred Learning Mode
              </Form.Label>
              <Form.Select
                isInvalid={!!errors.learningMode}
                {...register("learningMode")}
              >
                <option value="">Choose...</option>
                <option value="On-Campus">On-Campus Learning</option>
                <option value="Online">Online Learning</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.learningMode?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={12} className="mb-4">
            <Form.Group>
              <Form.Label className="fw-bold">
                Additional Message (Optional)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Tell us more about the student..."
                {...register("message")}
              />
            </Form.Group>
          </Col>
        </Row>

        <Button
          variant="primary"
          type="submit"
          size="lg"
          className="w-100 shadow-sm"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="ms-2">Processing...</span>
            </>
          ) : (
            "Submit Admission Request"
          )}
        </Button>
      </Form>
    </div>
  );
};

export default AdmissionsForm;
